import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ConfigCheck from '../../../../components/ConfigCheck'
import BiometricCheck from '../../../../components/BiometricCheck'
import ProctoringGuidelines from '../../../../components/ProctoringGuidelines'
import Assessment from './assessment'
import moment from 'moment'
import Timer from 'components/Timer'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import Bulb from '../../../../components/icons/Bulb'
import { Modal, Select, Tag, message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { fetchBehaviourInstructions as fetchInstructions, setCurrentAssessmentId, fetchBehaviourSpecialisations, saveBehaviourSpecialisation } from '../../actions'

import {
  PageContainer,
  ContentWrapper,
  StartButton,
  InstructionCard,
  InstructionHeader,
  InstructionTitle,
  InstructionDuration,
  InstructionDeadline,
  ImportantNotice,
  NoticeText,
  InstructionSectionTitle,
  InstructionList,
  InstructionListItem,
  TakeAssessmentButton,
  ContactInfo,
} from '../../Style'

const BehaviourInstructions = ({
  assessment,
  questions,
  totalQuestions,
  error,
  responses,
  fetchAssessmentQuestions,
  saveQuestionResponse,
  studentId,
  setAssessment,
}) => {
  const location = useLocation()

  // Group all useState hooks together at the top
  const [loading, setLoading] = useState(true)
  const [showConfig, setShowConfig] = useState(false)
  const [allChecksComplete, setAllChecksComplete] = useState(false)
  const [biometricRequested, setBiometricRequested] = useState(false)
  const [showAssessment, setShowAssessment] = useState(false)
  const [isTimerActive, setIsTimerActive] = useState(false)
  const [countdown, setCountdown] = useState(null)
  const [timerPhase, setTimerPhase] = useState('initial')
  const [selectedSpecialisations, setSelectedSpecialisations] = useState([])
  const [showSpecialisationModal, setShowSpecialisationModal] = useState(false)
  const [savingSpecialisation, setSavingSpecialisation] = useState(false)
  const dispatch = useDispatch()

  const MAX_SPECIALISATIONS = 2

  const verificationEnabled = assessment?.allowVerification !== false

  // Get student academic info from Redux
  const studentDetails = useSelector(state => state?.studentData?.studentDetails)
  const currentCourse = studentDetails?.currentCourse
  const educationLevel = currentCourse?.educationLevel || currentCourse?.education_level
  // Normalise DB enum literal "p.g.diploma" to JS identifier form "p_g_diploma"
  const normalisedLevel = educationLevel?.toLowerCase().replace(/\./g, '_')
  const isPGStudent = ['pg', 'phd', 'pd', 'p_g_diploma'].includes(normalisedLevel)
  const studentStreamId = currentCourse?.streamId || currentCourse?.stream_id

  // Get specialisations from Redux store
  const specialisations = useSelector(state => state?.assessment?.behaviourSpecialisations?.data || [])
  const specialisationsLoading = useSelector(state => state?.assessment?.behaviourSpecialisations?.loading || false)
  const mappedSpecialisationIds = useSelector(state => state?.assessment?.behaviourSpecialisations?.mappedIds || [])
  const savedSpecialisationIds = useSelector(state => state?.assessment?.behaviourSpecialisations?.savedIds || [])

  // Group useEffects together
  useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 1500)
  }, [])

  // Fetch specialisations for PG students
  useEffect(() => {
    if (isPGStudent && studentStreamId) {
      dispatch(fetchBehaviourSpecialisations(studentStreamId, assessment?.assessment_assigned_id))
    }
  }, [isPGStudent, studentStreamId, assessment?.assessment_assigned_id, dispatch])

  // Seed selection with mapped + previously-saved IDs once specialisations load
  useEffect(() => {
    if (!specialisations.length) return
    const validIds = new Set(specialisations.map(s => s.id))
    const seed = [...new Set([...mappedSpecialisationIds, ...savedSpecialisationIds])]
      .filter(id => validIds.has(id))
      .slice(0, MAX_SPECIALISATIONS)
    setSelectedSpecialisations(prev => (prev.length ? prev : seed))
  }, [specialisations, mappedSpecialisationIds, savedSpecialisationIds])

  useEffect(() => {
    if (assessment) {
      dispatch(setCurrentAssessmentId(assessment))
    }
  }, [assessment, dispatch])

  useEffect(() => {
    if (assessment?.assessment_set_id) {
      dispatch(fetchInstructions(assessment.assessment_set_id))
    }
  }, [assessment?.assessment_set_id, fetchAssessmentQuestions, dispatch])

  // Early return for loading state
  if (loading) {
    return <LoadingSpinner />
  }

  const handleTakeAssessment = async () => {
    // PG students with specialisations must pick one in the popup first
    if (isPGStudent && specialisations.length > 0) {
      // If admin already pre-selected the max number of specialisations,
      // skip the popup — persist admin's picks as the final selection and continue.
      const validIds = new Set(specialisations.map(s => s.id))
      const lockedPicks = mappedSpecialisationIds.filter(id => validIds.has(id))
      if (lockedPicks.length >= MAX_SPECIALISATIONS) {
        if (assessment?.assessment_assigned_id) {
          try {
            await dispatch(saveBehaviourSpecialisation(assessment.assessment_assigned_id, lockedPicks.slice(0, MAX_SPECIALISATIONS)))
          } catch (e) {
            message.error('Failed to save specialisation. Please try again.')
            return
          }
        }
        setShowConfig(true)
        return
      }
      setShowSpecialisationModal(true)
      return
    }
    setShowConfig(true)
  }

  const handleSpecialisationContinue = async () => {
    if (assessment?.assessment_assigned_id && selectedSpecialisations.length) {
      try {
        setSavingSpecialisation(true)
        await dispatch(saveBehaviourSpecialisation(assessment.assessment_assigned_id, selectedSpecialisations))
      } catch (e) {
        message.error('Failed to save specialisation. Please try again.')
        return
      } finally {
        setSavingSpecialisation(false)
      }
    }
    setShowSpecialisationModal(false)
    setShowConfig(true)
  }

  const handleSpecialisationChange = (values) => {
    // Never allow removing mapped IDs
    const locked = mappedSpecialisationIds.filter(id => !values.includes(id))
    const merged = [...new Set([...values, ...locked])]
    // Cap at MAX, but always keep mapped ones in the final list
    if (merged.length > MAX_SPECIALISATIONS) {
      message.warning(`You can select a maximum of ${MAX_SPECIALISATIONS} specialisations`)
      // Drop the most recent user pick that isn't locked
      const trimmed = merged.filter(id => mappedSpecialisationIds.includes(id))
      const extraPicks = merged.filter(id => !mappedSpecialisationIds.includes(id))
      const remaining = MAX_SPECIALISATIONS - trimmed.length
      setSelectedSpecialisations([...trimmed, ...extraPicks.slice(0, Math.max(0, remaining))])
      return
    }
    setSelectedSpecialisations(merged)
  }

  const handleCancelConfig = () => {
    setShowConfig(false)
  }

  const handleStart = async () => {
    if (!allChecksComplete) return

    if (assessment?.assessment_assigned_id) {
      const result = await fetchAssessmentQuestions(assessment.assessment_assigned_id)
      if (!result || !result.success) {
        console.error('Failed to fetch assessment questions')
        return
      }
    } else {
      return
    }

    setIsTimerActive(true)
    setShowConfig(false) // Close the modal
    setTimerPhase('initial')

    // Phase 1: Show "Timer starts now" for 2 seconds
    setTimeout(() => {
      setTimerPhase('countdown')
      setCountdown(3)

      // Phase 2: Start countdown
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            setTimerPhase('start')

            // Phase 3: Show Start! and begin assessment
            setTimeout(() => {
              setShowAssessment(true)
            }, 1000)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 2000)
  }

  if (showAssessment) {
    return (
      <Assessment
        questions={questions}
        totalQuestions={totalQuestions}
        loading={loading}
        error={error}
        responses={responses}
        saveQuestionResponse={saveQuestionResponse}
        startTime={new Date()}
        setAssessment={setAssessment}
      />
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  return (
    <PageContainer>
      <ContentWrapper>
        {isTimerActive ? (
          <Timer timerPhase={timerPhase} countdown={countdown} />
        ) : (
          <>
            {/* Add Back button at the top */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
                cursor: 'pointer',
              }}
              onClick={() => setAssessment(null)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px' }}
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="#4F46E5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#4F46E5',
                }}
              >
                Back to Assessments
              </span>
            </div>

            {/* Replace the conditional rendering with a Modal */}
            <Modal
              title="System & Biometric Check"
              open={showConfig}
              onCancel={handleCancelConfig}
              footer={null}
              width={700}
              centered
              maskClosable={false}
              style={{ top: 20 }}
            >
              {!allChecksComplete ? (
                <ConfigCheck
                  onAllChecksComplete={() => {
                    setAllChecksComplete(true)
                    if (verificationEnabled) {
                      setBiometricRequested(true)
                    }
                  }}
                />
              ) : verificationEnabled && biometricRequested ? (
                <BiometricCheck
                  studentId={studentId}
                  onStartAssessment={handleStart}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                    System Check Complete
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                    Your device is ready. Click below to start the assessment.
                  </p>
                  <button
                    onClick={handleStart}
                    style={{
                      background: '#4F46E5',
                      color: '#ffffff',
                      border: 'none',
                      padding: '12px 32px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Start Assessment
                  </button>
                </div>
              )}
            </Modal>

            <InstructionCard>
              <InstructionHeader>
                <div>
                  <InstructionTitle>Behavior Assessment</InstructionTitle>
                  <div
                    style={{
                      fontSize: '30px',
                      fontWeight: '700',
                      color: '#000000',
                    }}
                  >
                    {assessment?.title
                      ? assessment.title.charAt(0).toUpperCase() +
                        assessment.title.slice(1)
                      : ''}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InstructionDeadline
                    style={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '22px',
                      lineHeight: '132%',
                      letterSpacing: '0%',
                    }}
                  >
                    Ends on{'  '}
                    <span
                      style={{
                        color: '#3249D7',
                        marginLeft: '2px',
                        fontWeight: 'bold',
                      }}
                    >
                      {assessment?.endTime
                        ? moment.utc(assessment.endTime).format('Do MMMM, ha [IST]')
                        : '—'}
                    </span>
                  </InstructionDeadline>
                  <InstructionDuration>Duration - 30 mins</InstructionDuration>
                </div>
              </InstructionHeader>
            </InstructionCard>

            {/* Specialisation Selection Modal (PG students) */}
            <Modal
              title="Select your Specialisation"
              open={showSpecialisationModal}
              onCancel={() => setShowSpecialisationModal(false)}
              footer={null}
              width={520}
              centered
              maskClosable={false}
            >
              <div style={{ padding: '8px 4px 4px' }}>
                <p
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '16px',
                  }}
                >
                  Optionally choose up to {MAX_SPECIALISATIONS} specialisations to tailor your
                  report. Any already mapped to your profile are pre-selected and
                  locked. You can skip this step if you prefer.
                </p>
                <Select
                  mode="multiple"
                  placeholder="Select your specialisations"
                  style={{ width: '100%' }}
                  size="large"
                  value={selectedSpecialisations}
                  onChange={handleSpecialisationChange}
                  loading={specialisationsLoading}
                  showSearch
                  maxTagCount="responsive"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  tagRender={(props) => {
                    const { value, label, closable, onClose } = props
                    const isMapped = mappedSpecialisationIds.includes(value)
                    return (
                      <Tag
                        color={isMapped ? 'default' : 'blue'}
                        closable={closable && !isMapped}
                        onClose={onClose}
                        style={{
                          marginRight: 4,
                          marginTop: 2,
                          marginBottom: 2,
                          opacity: isMapped ? 0.85 : 1,
                        }}
                      >
                        {label}
                      </Tag>
                    )
                  }}
                  options={specialisations.map((spec) => ({
                    label: spec.name,
                    value: spec.id,
                    disabled:
                      mappedSpecialisationIds.includes(spec.id) ||
                      (selectedSpecialisations.length >= MAX_SPECIALISATIONS &&
                        !selectedSpecialisations.includes(spec.id)),
                  }))}
                />
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '8px',
                  }}
                >
                  {selectedSpecialisations.length}/{MAX_SPECIALISATIONS} selected
                  {mappedSpecialisationIds.length > 0 &&
                    ` • ${mappedSpecialisationIds.length} mapped from your profile`}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px',
                  }}
                >
                  <button
                    onClick={() => setShowSpecialisationModal(false)}
                    style={{
                      background: '#ffffff',
                      color: '#4F46E5',
                      border: '1px solid #4F46E5',
                      padding: '10px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSpecialisationContinue}
                    disabled={savingSpecialisation}
                    style={{
                      background: savingSpecialisation ? '#A5B4FC' : '#4F46E5',
                      color: '#ffffff',
                      border: 'none',
                      padding: '10px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: savingSpecialisation ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {savingSpecialisation
                      ? 'Saving...'
                      : selectedSpecialisations.length
                        ? 'Continue'
                        : 'Skip'}
                  </button>
                </div>
              </div>
            </Modal>

            <InstructionCard>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <div style={{ marginRight: '8px' }}>
                  <Bulb />
                </div>
                <span
                  style={{
                    color: '#4F46E5',
                    fontWeight: '600',
                    fontSize: '26px',
                  }}
                >
                  Important!
                </span>
                <span
                  style={{
                    marginLeft: '8px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '26px',
                    lineHeight: '132%',
                    letterSpacing: '0%',
                  }}
                >
                  Instructions
                </span>
              </div>

              <ImportantNotice>
                <NoticeText style={{ fontStyle: 'italic' }}>
                  Assessment has to be taken in one go, please do not exit once
                  started.
                </NoticeText>
              </ImportantNotice>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Technical Setup
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Ensure a stable internet connection.
                </InstructionListItem>
                <InstructionListItem>
                  Use a laptop/desktop equipped with a working webcam,
                  microphone, and speakers.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Environment
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Sit in a well-lit, quiet environment with minimal background
                  noise.
                </InstructionListItem>
                <InstructionListItem>
                  Avoid any distractions or interruptions during the assessment.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Behavior
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Do not switch tabs or use unauthorized resources.
                </InstructionListItem>
                <InstructionListItem>
                  Stay focused and remain within the assessment interface
                  throughout the session.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Monitoring
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  The system will monitor your screen, webcam, microphone, and
                  audio for proctoring purposes.
                </InstructionListItem>
                <InstructionListItem>
                  Ensure you are visibly present throughout the assessment.
                </InstructionListItem>
              </InstructionList>

            <ProctoringGuidelines proctoringEnabled={assessment?.allowProctoring !== false} />

              <ContactInfo
                style={{
                  textAlign: 'left',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                  fontStyle: 'italic',
                  marginBottom: '18',
                  marginLeft: '10px',
                }}
              >
                In case of technical issues, contact the assessment
                co-ordinator.
              </ContactInfo>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '24px',
                }}
              >
                <TakeAssessmentButton
                  onClick={handleTakeAssessment}
                  style={{
                    width: '302px',
                    height: '59px',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    padding: '0 24px',
                    marginTop: '24px',
                  }}
                >
                  Take assessment
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginLeft: '8px' }}
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </TakeAssessmentButton>
              </div>
            </InstructionCard>
          </>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default BehaviourInstructions
